import type {
  ShoppingHistoryEntry,
  ShoppingItemWriteInput,
  ShoppingListItem,
  ShoppingSuggestion,
} from '@recipes/contracts';
import {
  formatQuantityAmount,
  normalizeIngredientName,
  parseIngredientLine,
  rankSuggestions,
} from '@recipes/domain';
import { Button, Input, cn } from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { EyeOff, Pencil, Plus, RotateCcw } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { groceryCategoryLabel } from '@/features/shared/labels';
import { useFeature } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { currentLocale } from '@/lib/i18n';
import { offlineDb } from '@/lib/offline/db';
import { useShoppingHistory } from './hooks';

const LIMIT = 8;

/**
 * "2 kg Kartoffeln" → write input. No grocery category: the server takes it from the
 * ingredient catalog or the history, which know better than a keyword list.
 */
export function parseQuickAdd(line: string): ShoppingItemWriteInput {
  const parsed = parseIngredientLine(line);
  return {
    name: parsed.name,
    quantity: parsed.quantity,
    quantityMax: parsed.quantityMax,
    quantityText: parsed.quantityText,
    unitCode: parsed.unitCode,
    unitText: parsed.unitText,
    note: parsed.note,
  };
}

const fromHistory = (entry: ShoppingHistoryEntry, reactivates: boolean): ShoppingSuggestion => ({
  historyId: entry.id,
  name: entry.displayName,
  normalizedName: entry.normalizedName,
  groceryCategory: entry.groceryCategory,
  quantity: entry.lastQuantity,
  unitCode: entry.lastUnitCode,
  unitText: entry.lastUnitText,
  reactivates,
  source: 'HISTORY',
});

/** Suggestions from the device: the history first, then catalog ingredients. */
export function useLocalSuggestions(
  items: ShoppingListItem[],
  query: string,
): ShoppingSuggestion[] {
  const history = useShoppingHistory();
  const catalogEnabled = useFeature('ingredient_catalog');
  const needle = normalizeIngredientName(query);
  const ingredients = useLiveQuery(
    async () =>
      catalogEnabled && needle.length >= 2
        ? offlineDb.catalogIngredients
            .where('normalizedName')
            .startsWith(needle)
            .limit(LIMIT)
            .toArray()
        : [],
    [catalogEnabled, needle],
  );
  return useMemo(() => {
    const open = items.filter((i) => !i.checked).map((i) => i.normalizedName);
    const bought = items.filter((i) => i.checked).map((i) => i.normalizedName);
    const ranked = rankSuggestions(history, query, {
      openNames: open,
      boughtNames: bought,
      limit: LIMIT,
    }).map((r) => fromHistory(r.entry, r.reactivates));
    const taken = new Set([...open, ...ranked.map((s) => s.normalizedName)]);
    const hiddenNames = new Set(history.filter((h) => h.hidden).map((h) => h.normalizedName));
    for (const row of ingredients ?? []) {
      if (ranked.length >= LIMIT) break;
      if (taken.has(row.normalizedName) || hiddenNames.has(row.normalizedName)) continue;
      taken.add(row.normalizedName);
      ranked.push({
        historyId: null,
        name: row.ingredient.name,
        normalizedName: row.normalizedName,
        groceryCategory: row.ingredient.groceryCategory,
        quantity: null,
        unitCode: null,
        unitText: null,
        reactivates: bought.includes(row.normalizedName),
        source: 'CATALOG',
      });
    }
    return ranked;
  }, [history, ingredients, items, query]);
}

export function AddField({
  listId,
  items,
  disabled,
  busy,
  onAdd,
  onHide,
  onDetailed,
  trailing,
}: {
  listId: string;
  items: ShoppingListItem[];
  disabled?: boolean;
  busy?: boolean;
  onAdd: (input: ShoppingItemWriteInput) => void;
  onHide: (suggestion: ShoppingSuggestion) => void;
  onDetailed: () => void;
  /** Microphone button. */
  trailing?: ReactNode;
}) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const ids = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [debounced, setDebounced] = useState('');
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(text.trim()), 200);
    return () => clearTimeout(timer);
  }, [text]);

  const local = useLocalSuggestions(items, text.trim());
  // Online the server refines the result (trigram matches, the whole history).
  const remote = useQuery({
    queryKey: ['shopping', 'suggestions', listId, debounced],
    queryFn: () => api.shopping.suggestions(listId, debounced),
    enabled: open && online && Boolean(listId),
    staleTime: 10_000,
    retry: false,
  });
  const suggestions = useMemo(() => {
    const openNames = new Set(items.filter((i) => !i.checked).map((i) => i.normalizedName));
    const merged = [...local];
    if (remote.data && debounced === text.trim()) {
      const seen = new Set(merged.map((s) => s.normalizedName));
      for (const s of remote.data)
        if (!seen.has(s.normalizedName) && !openNames.has(s.normalizedName)) merged.push(s);
    }
    return merged.slice(0, LIMIT);
  }, [local, remote.data, debounced, text, items]);

  useEffect(() => setActive(-1), [text]);

  const submit = (input: ShoppingItemWriteInput) => {
    onAdd(input);
    setText('');
    setOpen(false);
  };
  const choose = (s: ShoppingSuggestion) =>
    submit({ name: s.name, quantity: s.quantity, unitCode: s.unitCode, unitText: s.unitText });
  const expanded = open && suggestions.length > 0;
  const listboxId = `${ids}-listbox`;

  return (
    <form
      className="relative mb-4 flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const chosen = expanded && active >= 0 ? suggestions[active] : undefined;
        if (chosen) choose(chosen);
        else if (text.trim()) submit(parseQuickAdd(text.trim()));
      }}
    >
      <label htmlFor="quick-add" className="sr-only">
        {t('shopping.quickAdd')}
      </label>
      <div className="relative flex-1">
        <Input
          id="quick-add"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={expanded && active >= 0 ? `${ids}-opt-${active}` : undefined}
          autoComplete="off"
          value={text}
          disabled={disabled}
          placeholder={t('shopping.quickAddPlaceholder')}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 150);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setActive((i) => (suggestions.length ? (i + 1) % suggestions.length : -1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => (suggestions.length ? (i <= 0 ? suggestions.length : i) - 1 : -1));
            } else if (e.key === 'Escape') {
              setOpen(false);
              setActive(-1);
            } else if (e.key === 'Delete' && e.shiftKey && expanded && active >= 0) {
              const s = suggestions[active];
              if (s?.historyId) {
                e.preventDefault();
                onHide(s);
              }
            }
          }}
          data-testid="shopping-quick-add"
        />
        <ul
          id={listboxId}
          role="listbox"
          aria-label={t('shopping.suggestions.label')}
          hidden={!expanded}
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-80 overflow-auto rounded-md border bg-card text-card-foreground shadow-md"
          onMouseDown={(e) => {
            // Keep the focus in the field; the blur timer must not close the list under the tap.
            e.preventDefault();
            clearTimeout(blurTimer.current);
          }}
          data-testid="shopping-suggestions"
        >
          {suggestions.map((s, index) => (
            <li
              key={s.normalizedName}
              id={`${ids}-opt-${index}`}
              role="option"
              aria-selected={index === active}
              className={cn(
                'flex min-h-[44px] cursor-pointer items-center gap-2 px-3 py-1 text-sm',
                index === active && 'bg-accent text-accent-foreground',
              )}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(s)}
              data-testid="shopping-suggestion"
            >
              {s.reactivates ? (
                <RotateCcw aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              ) : (
                <Plus aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  {s.name}
                  {s.quantity && (
                    <span className="text-muted-foreground">
                      {' · '}
                      {formatQuantityAmount(
                        { ...s, quantityMax: null, quantityText: null },
                        currentLocale(),
                      )}
                    </span>
                  )}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {s.reactivates
                    ? t('shopping.suggestions.reactivates')
                    : groceryCategoryLabel(t, s.groceryCategory)}
                </span>
              </span>
              {s.historyId && (
                <button
                  type="button"
                  tabIndex={-1}
                  className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                  aria-label={t('shopping.suggestions.hide', { name: s.name })}
                  title={t('shopping.suggestions.hide', { name: s.name })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onHide(s);
                  }}
                  data-testid="shopping-suggestion-hide"
                >
                  <EyeOff aria-hidden className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
      <Button
        type="submit"
        loading={busy}
        disabled={disabled || !text.trim()}
        aria-label={t('shopping.item.add')}
        data-testid="shopping-quick-add-submit"
      >
        <Plus aria-hidden />
        <span className="hidden sm:inline">{t('shopping.item.add')}</span>
      </Button>
      {trailing}
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={onDetailed}
        aria-label={t('shopping.item.addDetailed')}
      >
        <Pencil aria-hidden />
      </Button>
    </form>
  );
}
